import { isNativePlatform } from "@/native/platform";
import { cn } from "@/utils";
import { getCanvasHeroImageUrl } from "@cohesive/api-client";
import {
  getFileRecordIconInfo,
  pickFileRecordMediaURL,
  useThemeContext,
} from "@cohesive/ui-components";
import { formatDistanceToNow } from "date-fns";
import { Frame, Globe, MessagesSquare, Star, Users } from "lucide-react";
import React from "react";
import { RowOpenPill } from "../../components/RowOpenPill";
import { RowThumbnailDwellPreview } from "../../components/RowThumbnailDwellPreview";
import { formatFileSize } from "../../types";
import { handleWorkspaceItemActivate } from "../../workspaceItemActivate";
import {
  browserItemStarred,
  browserItemUnread,
  browserItemWorkspaceId,
} from "../utils/adapters";
import { browserItemId, type BrowserItem } from "../utils/homeBrowserTypes";
import {
  browserItemName,
  browserItemSize,
  browserItemTypeLabel,
  browserItemUpdatedAt,
} from "../utils/homeSort";
import { BrowserLocationCell } from "./BrowserLocationCell";
import { BROWSER_LIST_COL } from "./browserListColumns";

interface HomeBrowserListRowProps {
  item: BrowserItem;
  /** Item lives in a workspace the user doesn't own — leading Users glyph. */
  sharedWithMe?: boolean;
  onOpen: () => void;
  /** Pre-rendered registry menu (kebab + right-click portal, e.g.
   *  `ObjectActionCardMenu`) — grid cards and list rows share one
   *  manifest-driven action set. Pair with `menuOpen` (the controller's
   *  open state) and `onContextMenu`. */
  menuSlot?: React.ReactNode;
  /** External (registry controller) menu-open state — holds the
   *  timestamp→actions swap while the portaled menu is up. */
  menuOpen?: boolean;
  /** Right-click handler — opens the same registry menu as the kebab. */
  onContextMenu?: (e: React.MouseEvent) => void;
  /**
   * Workspace-selection wiring. When all three are provided the row uses the
   * page click model — single click selects (cmd/ctrl toggles, shift ranges),
   * double click / Enter opens. Without them a plain click opens directly.
   */
  selectionId?: string;
  isSelected?: boolean;
  onSelect?: (e: React.MouseEvent) => void;
}

/**
 * Leading 40px type tile — the old list view's size, so the Name column reads
 * as a vertical rail of previews. Hero/derived previews render as images
 * wrapped in the shared 500ms dwell zoom; everything else shows a glyph.
 */
function RowTile({ item, name }: { item: BrowserItem; name: string }) {
  const { mode } = useThemeContext();
  const frame =
    "flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-n-05 text-n-50";

  switch (item.kind) {
    case "canvas": {
      const heroUrl = getCanvasHeroImageUrl(item.data, mode);
      return (
        <RowThumbnailDwellPreview previewUrl={heroUrl ?? undefined} alt={name}>
          <div className={frame}>
            {heroUrl ? (
              // draggable=false: a native <img> drag advertises "Files" and
              // would light up the upload drop zones (rows drag via dnd-kit).
              <img
                src={heroUrl}
                alt=""
                draggable={false}
                className="h-full w-full object-cover"
              />
            ) : (
              <Frame className="size-4" />
            )}
          </div>
        </RowThumbnailDwellPreview>
      );
    }
    case "file": {
      const contentType = item.data.content_type || "application/octet-stream";
      const previewUrl =
        pickFileRecordMediaURL(
          item.data as unknown as Parameters<typeof pickFileRecordMediaURL>[0],
          "thumbnail",
        ) ?? undefined;
      // Any derived preview renders as an image — the server derives JPGs for
      // PDFs / docs / captures too, and showing the actual page beats a
      // mime-type glyph (same heuristic as the grid cards). Types with no
      // derivative fall back to a fixed size-4 glyph. We render the glyph
      // directly rather than via FileRecordThumbnail, whose icon fallback is
      // sized in container-query units for the large grid cards and overflows
      // this tile (there's no container context here).
      if (previewUrl) {
        return (
          <RowThumbnailDwellPreview previewUrl={previewUrl} alt={name}>
            <div className={frame}>
              <img
                src={previewUrl}
                alt=""
                draggable={false}
                className="h-full w-full object-cover"
              />
            </div>
          </RowThumbnailDwellPreview>
        );
      }
      const { icon: FileGlyph, color } = getFileRecordIconInfo(
        contentType,
        item.data.origin_type,
      );
      return (
        <div className={frame}>
          <FileGlyph className={cn("size-4", color)} />
        </div>
      );
    }
    case "chat":
      return (
        <div className={frame}>
          <MessagesSquare className="size-4" />
        </div>
      );
  }
}

/**
 * Unified browser list row, in the shared `BROWSER_LIST_COL` geometry:
 * 40px tile · shared indicator + name (+ star, unread dot) · type (wide
 * mode only) · size · last-updated (which swaps to the Open pill + kebab on
 * hover). The Type and Size columns shed as the list narrows (see
 * `browserListColumns`).
 */
export function HomeBrowserListRow({
  item,
  sharedWithMe,
  onOpen,
  menuSlot,
  menuOpen,
  onContextMenu,
  selectionId,
  isSelected,
  onSelect,
}: HomeBrowserListRowProps) {
  const name = browserItemName(item);
  /** Published files carry the old list view's Globe treatment. */
  const published = item.kind === "file" && !!item.data.published;
  const updatedAt = browserItemUpdatedAt(item);
  const modified = updatedAt
    ? formatDistanceToNow(new Date(updatedAt), { addSuffix: true }).replace(
        /^about /,
        "",
      )
    : "—";
  const sizeBytes = browserItemSize(item);
  const starred = browserItemStarred(item);
  const unread = browserItemUnread(item);
  const selectable = !!(selectionId && onSelect);
  // The registry menu reports open state via prop; it holds the
  // timestamp→actions hover swap below.
  const menuHeld = !!menuOpen;
  // Native has no hover: the kebab is pinned open and the timestamp keeps its
  // place beside it. On web (mouse or touch) the hover reveal is unchanged.
  const native = isNativePlatform();

  return (
    <div
      data-testid={browserItemId(item)}
      role="button"
      tabIndex={0}
      aria-selected={selectable ? isSelected : undefined}
      /* No `data-workspace-item` stamp here: selectable rows are wrapped in
         `SelectableItem`, which owns the selection/drag attributes — same
         single-source contract as the grid cards. */
      onClick={
        selectable
          ? (e) =>
              handleWorkspaceItemActivate({
                isSelected: !!isSelected,
                e,
                onOpen,
                onSelect: () => onSelect(e),
              })
          : onOpen
      }
      onDoubleClick={selectable ? onOpen : undefined}
      onContextMenu={onContextMenu}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        /* -ml + width comp: the leading gutter hangs 6px into the page
           padding while the trailing edge stays aligned with the toolbar. */
        "group -ml-1.5 flex min-h-12 w-[calc(100%+6px)] cursor-pointer items-center gap-3 rounded-md py-1.5 pr-2.5 text-left outline-none transition-colors",
        /* Base bg is the list's zebra stripe (see `HomeBrowserList`); hover
           is n-10 — one ladder step above the n-05 page ground, where n-05
           itself was invisible. Full-strength so striped and plain rows
           hover identically; the selected rust below replaces the stripe
           via tailwind-merge. */
        "bg-[var(--row-stripe,transparent)]",
        "hover:bg-n-10 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]",
        /* Double-click must not paint a native text selection. */
        selectable && "select-none",
        selectable && isSelected && "bg-rust/10 hover:bg-rust/10",
      )}
    >
      {/* Blank gutter matching the folder rows' expand-chevron gutter, so
          folder and item tiles sit in one vertical rail. The -mr-1.5 halves
          the row's gap-3 after it, keeping the indent modest. */}
      <span aria-hidden className="-mr-1.5 w-3.5 shrink-0" />
      <RowTile item={item} name={name} />

      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        {/* Sharing state, left of the title: Users = lives in a workspace
            shared with me; Globe = the item itself is shared (published) —
            main's treatment on the old workspace detail rows. */}
        {sharedWithMe ? (
          <span
            title="Shared with me"
            className="inline-flex shrink-0 items-center text-muted-foreground/70"
          >
            <Users aria-label="Shared with me" className="size-3" />
          </span>
        ) : published ? (
          <span
            title="Published"
            className="inline-flex shrink-0 items-center text-muted-foreground/70"
          >
            <Globe aria-label="Published" className="size-3" />
          </span>
        ) : null}
        <span
          className={cn(
            "truncate text-sm text-foreground",
            unread && "font-semibold",
          )}
        >
          {name}
        </span>
        {starred && (
          <Star
            aria-label="Favorite"
            className="size-3 shrink-0 fill-gold text-gold"
          />
        )}
        {unread && (
          <span
            aria-label="Unread"
            className="size-1.5 shrink-0 rounded-full bg-rust"
          />
        )}
      </span>

      {/* Files can nest inside a chat or a canvas (`FileRecordClass`); the
          cell shows the most specific container. Other kinds sit directly
          in their workspace. */}
      <BrowserLocationCell
        workspaceId={browserItemWorkspaceId(item)}
        canvasId={item.kind === "file" ? item.data.canvas_id : undefined}
        flowSessionId={
          item.kind === "file" ? item.data.flow_session_id : undefined
        }
      />

      <span
        className={cn(
          BROWSER_LIST_COL.type,
          "truncate text-xs text-muted-foreground",
        )}
      >
        {browserItemTypeLabel(item)}
      </span>

      <span
        className={cn(
          BROWSER_LIST_COL.size,
          "truncate text-xs text-muted-foreground",
        )}
      >
        {sizeBytes !== undefined ? formatFileSize(sizeBytes) : "—"}
      </span>

      {/* Trailing cell: the timestamp at rest, swapped for the Open pill +
          kebab on hover (Gmail-style) so the actions need no column of their
          own. `menuHeld` holds the swap while the menu is up — the pointer
          leaves the row for the portaled menu, which would otherwise hide
          the menu's own trigger. Native has no hover, so there the kebab is
          always shown beside the timestamp (and the Open pill stays hidden —
          a tap already opens the row). */}
      <span className={cn(BROWSER_LIST_COL.modified, "flex items-center")}>
        <span
          className={cn(
            "truncate text-xs text-muted-foreground",
            menuHeld
              ? "hidden"
              : cn(!native && "group-hover:hidden", native && "inline"),
          )}
        >
          {modified}
        </span>
        {/* Left-justified so the pill lines up with the timestamp it replaces. */}
        <span
          className={cn(
            "w-full items-center gap-1",
            native && "ml-auto w-auto shrink-0",
            menuHeld ? "flex" : cn("hidden group-hover:flex", native && "flex"),
          )}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <RowOpenPill label={name} onOpen={onOpen} />
          {menuSlot}
        </span>
      </span>
    </div>
  );
}
